import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import io from "socket.io-client";
import {
  fetchOrders,
  updateOrderStatus,
  assignRiderToOrder,
  fetchRidersAdmin,
  updateOrderItems,
  fetchProducts,
  updateOrderAddItem,
  updateOrderItemQty,
  deleteOrderItem,
  confirmOrderAndShip,
} from "../../api/api";
import api from "../../api/api";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Avatar,
  Tooltip,
  Skeleton,
  InputBase,
  Select,
  MenuItem,
  Drawer,
  Divider,
  TextField,
  Stack,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  OutlinedInput,
  Pagination,
  Badge,
  LinearProgress,
  Collapse,
  CircularProgress,
} from "@mui/material";
import {
  Refresh,
  Search,
  Close,
  LocalShipping,
  Receipt,
  DirectionsBike,
  FileDownload,
  FilterList,
  ExpandMore,
  ExpandLess,
  CheckCircleOutline,
  RadioButtonUnchecked,
  OpenInNew,
  CalendarToday,
  Person,
  LocationOn,
  Inventory2,
  CreditCard,
  Timeline,
  Edit,
  Add,
  Delete,
  Remove,
  Save,
  Phone,
  ContentCopy,
  LocalShippingOutlined,
  Print,
  TrendingUp,
  MyLocation,
  AttachMoney,
  FiberManualRecord,
  WifiOff,
  NotificationsActive,
} from "@mui/icons-material";
import { useSnackbar } from "../../utils/snackbar";

// ─── Status Config ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  created:   { label: "Created",   color: "#64748b", bg: "rgba(100,116,139,0.12)", border: "rgba(100,116,139,0.3)" },
  paid:      { label: "Paid",      color: "#3b82f6", bg: "rgba(59,130,246,0.12)",  border: "rgba(59,130,246,0.3)"  },
  shipped:   { label: "Shipped",   color: "#f59e0b", bg: "rgba(245,158,11,0.12)",  border: "rgba(245,158,11,0.3)"  },
  completed: { label: "Completed", color: "#22c55e", bg: "rgba(34,197,94,0.12)",   border: "rgba(34,197,94,0.3)"   },
  cancelled: { label: "Cancelled", color: "#ef4444", bg: "rgba(239,68,68,0.12)",   border: "rgba(239,68,68,0.3)"   },
};

const PAYMENT_CONFIG = {
  mpesa:    { label: "M-Pesa",    color: "#22c55e" },
  cash:     { label: "Cash",      color: "#f59e0b" },
  card:     { label: "Card",      color: "#3b82f6" },
  bank:     { label: "Bank",      color: "#6366f1" },
};

const STATUS_TABS = ["all", "created", "paid", "shipped", "completed", "cancelled"];
const DEFAULT_PAGE_SIZE = 20;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const kes = (n) => `KSh ${Math.round(n || 0).toLocaleString()}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-KE", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-KE", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const shortId = (id) => `#${(id || "").slice(-6).toUpperCase()}`;

const timeAgo = (iso) => {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return fmtDate(iso);
};

const COMPANY = { name: "MTAANI", tag: "Powered by Mtaani Technologies", vatRate: 0.16 };

const getNextReceiptNumber = () => {
  const key = "mtaani_receipt_counter";
  const n = parseInt(localStorage.getItem(key) || "0", 10) + 1;
  localStorage.setItem(key, String(n));
  return String(n).padStart(6, "0");
};

const printReceipt = async (order, cashTendered = 0) => {
  const receiptNo = getNextReceiptNumber();
  const items = order.items || [];
  const subtotal = items.reduce((s, i) => s + (i.priceAtPurchase || 0) * (i.quantity || 1), 0);
  const deliveryFee = order.deliveryFee || 0;
  const discount = order.discount || 0;
  const total = order.total || subtotal + deliveryFee - discount;
  const vat = total * COMPANY.vatRate;
  const change = cashTendered > 0 ? Math.max(0, cashTendered - total) : 0;
  const qrText = `${shortId(order._id)} | ${order.user?.name || "Customer"} | KSh ${Math.round(total)}`;
  const qrDataUrl = await QRCode.toDataURL(qrText, { width: 128, margin: 1 });

  const html = `<!DOCTYPE html><html><head><title>Receipt #${receiptNo}</title>
  <style>
    body{font-family:'Courier New',monospace;font-size:12px;max-width:300px;margin:0 auto;padding:12px}
    .c{text-align:center}.b{font-weight:bold}.s{border-top:1px dashed #000;margin:6px 0}
    .r{display:flex;justify-content:space-between;margin:2px 0}.big{font-size:15px}
  </style></head><body>
  <div class="c b big">${COMPANY.name}</div>
  <div class="c" style="font-size:10px">${COMPANY.tag}</div>
  <div class="c" style="font-size:10px">Receipt #${receiptNo}</div>
  <div class="c" style="font-size:10px">${fmtDateTime(order.createdAt)}</div>
  <div class="s"></div>
  <div class="r"><span>Customer:</span><span>${order.user?.name || "Walk-in"}</span></div>
  <div class="r"><span>Order:</span><span>${shortId(order._id)}</span></div>
  <div class="r"><span>Payment:</span><span>${(order.paymentMethod || "—").toUpperCase()}</span></div>
  <div class="s"></div>
  ${items.map((i) => `
    <div>${i.product?.title || i.product?.name || "Item"}</div>
    <div class="r"><span>  ${i.quantity} × KSh ${Math.round(i.priceAtPurchase || 0)}</span><span>KSh ${Math.round((i.priceAtPurchase || 0) * (i.quantity || 1))}</span></div>
  `).join("")}
  <div class="s"></div>
  <div class="r"><span>Subtotal</span><span>KSh ${Math.round(subtotal)}</span></div>
  ${deliveryFee > 0 ? `<div class="r"><span>Delivery</span><span>KSh ${Math.round(deliveryFee)}</span></div>` : ""}
  ${discount > 0 ? `<div class="r"><span>Discount</span><span>-KSh ${Math.round(discount)}</span></div>` : ""}
  <div class="r"><span>VAT (${Math.round(COMPANY.vatRate * 100)}%)</span><span>KSh ${Math.round(vat)}</span></div>
  <div class="s"></div>
  <div class="r b big"><span>TOTAL</span><span>KSh ${Math.round(total)}</span></div>
  ${cashTendered > 0 ? `
    <div class="r"><span>Cash Tendered</span><span>KSh ${Math.round(cashTendered)}</span></div>
    <div class="r b"><span>Change</span><span>KSh ${Math.round(change)}</span></div>
  ` : ""}
  <div class="s"></div>
  <div class="c"><img src="${qrDataUrl}" width="120" height="120" /></div>
  <div class="c" style="font-size:10px;margin-top:6px">Thank you for shopping with us!</div>
  </body></html>`;

  const win = window.open("", "_blank", "width=420,height=650");
  win.document.write(html);
  win.document.close();
  setTimeout(() => win.print(), 400);
};

const buildOrderDiff = (original, edited) => {
  const added   = edited.filter((i) => i._new && !i._del).length;
  const removed = edited.filter((i) => i._del && !i._new).length;
  const changed = edited.filter((i) => !i._new && !i._del).filter((ei) => {
    const orig = original.find((o) => o._id === ei._id);
    return orig && orig.quantity !== ei.quantity;
  }).length;
  return { added, removed, changed, total: added + removed + changed };
};

// ─── CSV Export ───────────────────────────────────────────────────────────────
function exportCSV(orders) {
  const rows = [
    ["Order ID", "Customer", "Phone", "Email", "Items", "Total (KSh)", "Payment", "Status", "Created"],
    ...orders.map((o) => [
      shortId(o._id),
      o.user?.name || "Unknown",
      o.user?.phone || "",
      o.user?.email || "",
      (o.items || []).length,
      Math.round(o.total || 0),
      o.paymentMethod || "",
      o.status || "",
      fmtDateTime(o.createdAt),
    ]),
  ];
  const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `orders-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Status Chip ──────────────────────────────────────────────────────────────
function StatusChip({ status, size = "small" }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.created;
  return (
    <Chip
      label={cfg.label}
      size={size}
      sx={{
        bgcolor: cfg.bg,
        color: cfg.color,
        border: `1px solid ${cfg.border}`,
        fontWeight: 700,
        fontSize: size === "small" ? 11 : 12,
        height: size === "small" ? 24 : 28,
      }}
    />
  );
}

// ─── Payment Chip ─────────────────────────────────────────────────────────────
function PaymentChip({ method }) {
  const cfg = PAYMENT_CONFIG[method?.toLowerCase()] || { label: method || "—", color: "#94a3b8" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      icon={<CreditCard sx={{ fontSize: "12px !important", color: `${cfg.color} !important` }} />}
      sx={{
        bgcolor: `${cfg.color}18`,
        color: cfg.color,
        border: `1px solid ${cfg.color}44`,
        fontWeight: 600,
        fontSize: 11,
        height: 24,
      }}
    />
  );
}

// ─── Stat Chip ────────────────────────────────────────────────────────────────
function StatChip({ label, count, color, onClick, active }) {
  return (
    <Chip
      label={`${label} · ${count}`}
      size="small"
      onClick={onClick}
      sx={{
        fontWeight: 700,
        fontSize: 12,
        height: 30,
        bgcolor: active ? `${color}22` : "background.paper",
        color: active ? color : "text.secondary",
        border: `1px solid ${active ? color + "55" : "divider"}`,
        cursor: "pointer",
        "&:hover": { bgcolor: `${color}18`, color },
        transition: "all 0.15s",
      }}
    />
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KPICard({ label, value, subtext, color, icon: Icon, active, onClick }) {
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        flex: 1, minWidth: 130, p: 1.8, borderRadius: 2, cursor: onClick ? "pointer" : "default",
        borderColor: active ? color : "divider",
        bgcolor: active ? `${color}0d` : "background.paper",
        transition: "all 0.15s",
        "&:hover": onClick ? { borderColor: color, bgcolor: `${color}0d` } : {},
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.8 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6 }}>
          {label}
        </Typography>
        <Box sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: `${color}20`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon sx={{ fontSize: 15, color }} />
        </Box>
      </Box>
      <Typography sx={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1.1 }}>{value}</Typography>
      {subtext && <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.4 }}>{subtext}</Typography>}
    </Paper>
  );
}

// ─── Loading Skeleton Row ─────────────────────────────────────────────────────
function SkeletonRow() {
  return (
    <TableRow>
      {[44, 120, 160, 80, 100, 110, 100, 110, 80, 60].map((w, i) => (
        <TableCell key={i} sx={{ py: 1.5 }}>
          <Skeleton variant="rounded" width={w} height={20} />
        </TableCell>
      ))}
    </TableRow>
  );
}

// ─── Order Detail Drawer ──────────────────────────────────────────────────────
function OrderDetailDrawer({
  order,
  riders,
  open,
  onClose,
  onStatusChange,
  onRiderAssign,
  onItemsSaved,
  actionLoading,
}) {
  const navigate = useNavigate();
  const [newStatus, setNewStatus] = useState(order?.status || "created");
  const [newRider, setNewRider]   = useState(order?.rider?._id || "");

  // Cash tendered for receipt printing
  const [cashTendered, setCashTendered] = useState(0);
  const [printLoading, setPrintLoading] = useState(false);

  // Edit-items state
  const [editMode, setEditMode]           = useState(false);
  const [editItems, setEditItems]         = useState([]);
  const [products, setProducts]           = useState([]);
  const [prodsLoading, setProdsLoading]   = useState(false);
  const [productQuery, setProductQuery]   = useState("");
  const [addQty, setAddQty]               = useState(1);
  const [editSaving, setEditSaving]       = useState(false);
  const [editError, setEditError]         = useState("");

  // Pre-shipment confirmation state
  const [confirmNote, setConfirmNote]     = useState("");
  const [confirmSaving, setConfirmSaving] = useState(false);

  useEffect(() => {
    setNewStatus(order?.status || "created");
    setNewRider(order?.rider?._id || "");
    setEditMode(false);
    setEditItems([]);
    setProductQuery("");
    setAddQty(1);
    setEditError("");
    setConfirmNote("");
    setConfirmSaving(false);
    setCashTendered(0);
  }, [order?._id, order?.status, order?.rider?._id]);

  // Pre-load products as soon as an editable order opens
  useEffect(() => {
    if (!open || !["created", "paid"].includes(order?.status) || products.length || prodsLoading) return;
    setProdsLoading(true);
    fetchProducts()
      .then((res) => {
        const raw = res.data;
        setProducts(
          Array.isArray(raw) ? raw
            : Array.isArray(raw?.products) ? raw.products
            : Array.isArray(raw?.data) ? raw.data
            : []
        );
      })
      .catch(() => {})
      .finally(() => setProdsLoading(false));
  }, [open, order?._id]);

  const productSuggestions = useMemo(() => {
    const q = productQuery.trim().toLowerCase();
    if (!q) return [];
    return products.filter((p) =>
      (p.title || p.name || "").toLowerCase().includes(q) ||
      (p.category?.name || "").toLowerCase().includes(q) ||
      (p.sku || p.barcode || "").toLowerCase().includes(q)
    ).slice(0, 8);
  }, [products, productQuery]);

  if (!order) return null;

  const items       = order.items || [];
  const canEdit     = ["created", "paid"].includes(order.status);
  const subtotal    = items.reduce((s, i) => s + (i.priceAtPurchase || 0) * (i.quantity || 1), 0);
  const deliveryFee = order.deliveryFee || 0;
  const discount    = order.discount || 0;
  const total       = order.total || subtotal + deliveryFee - discount;
  const addr        = order.shippingAddress?.addressLine || order.shippingAddress?.address || order.address || "—";
  const isLoading   = !!actionLoading[order._id];

  // ── Edit-items handlers ──────────────────────────────────────────────────
  const startEdit = () => {
    setEditItems(items.map((i) => ({ ...i, _new: false, _del: false })));
    setEditMode(true);
    setEditError("");
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditItems([]);
    setProductQuery("");
    setAddQty(1);
    setEditError("");
  };

  const adjustQty = (id, delta) =>
    setEditItems((prev) => prev.map((i) => i._id === id ? { ...i, quantity: Math.max(1, (i.quantity || 1) + delta) } : i));

  const setExactQty = (id, val) => {
    const q = parseInt(val, 10);
    if (!isNaN(q) && q >= 1) setEditItems((prev) => prev.map((i) => i._id === id ? { ...i, quantity: q } : i));
  };

  const toggleDel = (id) =>
    setEditItems((prev) => prev.map((i) => i._id === id ? { ...i, _del: !i._del } : i));

  const addItemFromSuggestion = (product) => {
    const dup = editItems.find((i) => !i._del && i.product?._id === product._id);
    if (dup) {
      setEditItems((prev) => prev.map((i) => i._id === dup._id ? { ...i, quantity: i.quantity + addQty } : i));
    } else {
      setEditItems((prev) => [
        ...prev,
        { _id: `_new_${Date.now()}`, _new: true, _del: false, product, quantity: addQty, priceAtPurchase: product.price || 0 },
      ]);
    }
    setProductQuery("");
    setAddQty(1);
  };

  const saveEdits = async () => {
    if (editItems.filter((i) => !i._del).length === 0) {
      setEditError("Order must have at least one item.");
      return;
    }
    setEditSaving(true);
    setEditError("");
    try {
      const calls = [];
      for (const ei of editItems) {
        if (ei._new) {
          calls.push(updateOrderAddItem(order._id, { productId: ei.product._id, quantity: ei.quantity, price: ei.priceAtPurchase }));
        } else if (ei._del) {
          calls.push(deleteOrderItem(order._id, ei._id));
        } else {
          const orig = items.find((i) => i._id === ei._id);
          if (orig && orig.quantity !== ei.quantity) calls.push(updateOrderItemQty(order._id, ei._id, { quantity: ei.quantity }));
        }
      }
      if (calls.length) await Promise.all(calls);
      await onItemsSaved(order._id);
      cancelEdit();
    } catch (err) {
      setEditError(err?.response?.data?.message || "Failed to update order items.");
    } finally {
      setEditSaving(false);
    }
  };

  const handleConfirmAndShip = async () => {
    setConfirmSaving(true);
    setEditError("");
    try {
      await confirmOrderAndShip(order._id, confirmNote);
      await onItemsSaved(order._id);
      setConfirmNote("");
    } catch (err) {
      setEditError(err?.response?.data?.message || "Failed to confirm order.");
    } finally {
      setConfirmSaving(false);
    }
  };

  const handlePrintReceipt = async () => {
    setPrintLoading(true);
    try {
      await printReceipt(order, cashTendered);
    } finally {
      setPrintLoading(false);
    }
  };

  const handleTrackRider = () => {
    if (order.rider?._id) navigate(`/admin/live-tracking?rider=${order.rider._id}&order=${order._id}`);
  };

  const displayItems = editMode ? editItems : items;
  const activeCount  = editMode ? editItems.filter((i) => !i._del).length : items.length;
  const diff = editMode ? buildOrderDiff(items, editItems) : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480, md: 520 },
          bgcolor: "background.paper",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      {/* Drawer Header */}
      <Box sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", gap: 1.5, bgcolor: "background.default", flexShrink: 0 }}>
        <Receipt sx={{ color: "primary.main", fontSize: 20 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={800} noWrap>Order {shortId(order._id)}</Typography>
          <Typography variant="caption" color="text.secondary">{fmtDateTime(order.createdAt)} · {timeAgo(order.createdAt)}</Typography>
        </Box>
        <StatusChip status={order.status} />
        <Tooltip title="Print Receipt">
          <IconButton
            size="small"
            onClick={handlePrintReceipt}
            disabled={printLoading}
            sx={{ borderRadius: 1.5, color: "text.secondary", "&:hover": { color: "primary.main", bgcolor: "action.hover" } }}
          >
            {printLoading ? <CircularProgress size={14} /> : <Print sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={onClose} sx={{ ml: 0.5 }}><Close fontSize="small" /></IconButton>
      </Box>

      {/* Scrollable Body */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 2.5 }}>
        <Stack spacing={2.5}>
          {/* ── Process Order (3-step guide) ──────────────────────────────── */}
          {canEdit && (
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
              {/* Header */}
              <Box sx={{
                px: 2, py: 1.4,
                background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                display: "flex", alignItems: "center", gap: 1,
              }}>
                <LocalShippingOutlined sx={{ fontSize: 16, color: "white" }} />
                <Typography variant="caption" fontWeight={800}
                  sx={{ color: "white", textTransform: "uppercase", letterSpacing: 0.8, flex: 1 }}>
                  Process Order
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)", fontSize: 11 }}>
                  Follow steps 1 → 2 → 3
                </Typography>
              </Box>

              <Stack divider={<Divider />}>
                {/* ── Step 1: Review / edit items ──────────────────────── */}
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.2 }}>
                    <Box sx={{
                      width: 22, height: 22, borderRadius: "50%", bgcolor: "primary.main",
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                      <Typography sx={{ color: "white", fontSize: 11, fontWeight: 800, lineHeight: 1 }}>1</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={700}>Review &amp; edit items</Typography>
                    <Chip label="optional" size="small"
                      sx={{ ml: "auto", fontSize: 10, height: 18, bgcolor: "background.default" }} />
                  </Box>

                  <Box sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    bgcolor: "background.default", p: 1.5, borderRadius: 1.5,
                    border: "1px solid", borderColor: "divider",
                  }}>
                    <Box>
                      <Typography variant="body2" fontWeight={700} component="span">{activeCount}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mx: 0.5 }}>
                        {activeCount === 1 ? "item" : "items"}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ mx: 0.3 }}>·</Typography>
                      <Typography variant="body2" fontWeight={700} color="success.main" component="span">
                        {kes(total)}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant={editMode ? "contained" : "outlined"}
                      color={editMode ? "success" : "primary"}
                      startIcon={<Edit sx={{ fontSize: 13 }} />}
                      onClick={editMode ? cancelEdit : startEdit}
                      disabled={isLoading}
                      sx={{ borderRadius: 1.5, fontSize: 12, minWidth: 0 }}
                    >
                      {editMode ? "Done editing" : "Edit Items"}
                    </Button>
                  </Box>
                </Box>

                {/* ── Step 2: Call customer ────────────────────────────── */}
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.2 }}>
                    <Box sx={{
                      width: 22, height: 22, borderRadius: "50%", bgcolor: "primary.main",
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                      <Typography sx={{ color: "white", fontSize: 11, fontWeight: 800, lineHeight: 1 }}>2</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={700}>Call customer to confirm</Typography>
                    <Chip label="required" size="small" color="warning"
                      sx={{ ml: "auto", fontSize: 10, height: 18 }} />
                  </Box>

                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    bgcolor: "background.default", p: 1.5, borderRadius: 1.5,
                    border: "1px solid", borderColor: "divider",
                  }}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: "primary.main", fontSize: 14, fontWeight: 800, flexShrink: 0 }}>
                      {(order.user?.name || "C").charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700} noWrap>
                        {order.user?.name || "Customer"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: "monospace", fontWeight: 600, color: order.user?.phone ? "text.primary" : "error.main" }}
                      >
                        {order.user?.phone || "⚠ No phone on file"}
                      </Typography>
                    </Box>
                    {order.user?.phone ? (
                      <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                        <Button
                          variant="contained"
                          size="small"
                          component="a"
                          href={`tel:${order.user.phone}`}
                          startIcon={<Phone sx={{ fontSize: 13 }} />}
                          sx={{ borderRadius: 1.5, fontSize: 12, bgcolor: "#22c55e", "&:hover": { bgcolor: "#16a34a" } }}
                        >
                          Call
                        </Button>
                        <Tooltip title="Copy number">
                          <IconButton size="small"
                            onClick={() => navigator.clipboard.writeText(order.user.phone)}
                            sx={{ borderRadius: 1.5, border: "1px solid", borderColor: "divider" }}>
                            <ContentCopy sx={{ fontSize: 13 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Chip label="No phone" size="small" color="error" sx={{ fontSize: 10, height: 22 }} />
                    )}
                  </Box>
                </Box>

                {/* ── Step 3: Confirm & ship ───────────────────────────── */}
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.2 }}>
                    <Box sx={{
                      width: 22, height: 22, borderRadius: "50%", bgcolor: "#22c55e",
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                      <Typography sx={{ color: "white", fontSize: 11, fontWeight: 800, lineHeight: 1 }}>3</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={700}>Confirm &amp; dispatch</Typography>
                  </Box>

                  <TextField
                    multiline
                    rows={2}
                    fullWidth
                    size="small"
                    placeholder="Call notes (optional) — e.g. customer confirmed, removed 1×bread, added 2L water…"
                    value={confirmNote}
                    onChange={(e) => setConfirmNote(e.target.value)}
                    sx={{ mb: 1.5, "& .MuiInputBase-root": { borderRadius: 1.5, fontSize: 12.5 } }}
                  />

                  {editError && (
                    <Alert severity="error" onClose={() => setEditError("")}
                      sx={{ mb: 1.5, borderRadius: 1.5, py: 0.5, fontSize: 12 }}>
                      {editError}
                    </Alert>
                  )}

                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      disabled={confirmSaving || isLoading}
                      onClick={() => onStatusChange(order._id, "cancelled")}
                      sx={{ borderRadius: 1.5, fontWeight: 700, fontSize: 12 }}
                    >
                      Cancel Order
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={confirmSaving || isLoading}
                      onClick={handleConfirmAndShip}
                      startIcon={confirmSaving
                        ? <CircularProgress size={12} color="inherit" />
                        : <LocalShippingOutlined sx={{ fontSize: 15 }} />
                      }
                      sx={{
                        flex: 1, borderRadius: 1.5, fontWeight: 700, fontSize: 12,
                        bgcolor: "#22c55e",
                        "&:hover": { bgcolor: "#16a34a" },
                        "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
                      }}
                    >
                      {confirmSaving ? "Confirming…" : "Confirm & Ship"}
                    </Button>
                  </Box>
                </Box>
              </Stack>
            </Paper>
          )}

          {/* Customer */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <Person sx={{ fontSize: 16, color: "primary.main" }} />
              <Typography variant="caption" fontWeight={700} color="primary" sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}>Customer</Typography>
            </Box>
            <Stack spacing={0.6}>
              <Typography variant="body2" fontWeight={700}>{order.user?.name || "Unknown"}</Typography>
              {order.user?.email && <Typography variant="caption" color="text.secondary">{order.user.email}</Typography>}
              {order.user?.phone && <Typography variant="caption" color="text.secondary">{order.user.phone}</Typography>}
            </Stack>
          </Paper>

          {/* Shipping */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <LocationOn sx={{ fontSize: 16, color: "primary.main" }} />
              <Typography variant="caption" fontWeight={700} color="primary" sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}>Shipping Address</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">{addr}</Typography>
            {order.shippingAddress?.city && (
              <Typography variant="caption" color="text.secondary">
                {[order.shippingAddress.city, order.shippingAddress.country].filter(Boolean).join(", ")}
              </Typography>
            )}
          </Paper>

          {/* Items */}
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
            {/* Items header */}
            <Box sx={{ px: 2, py: 1.5, bgcolor: "background.default", borderBottom: "1px solid", borderColor: "divider" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Inventory2 sx={{ fontSize: 16, color: "primary.main" }} />
                <Typography variant="caption" fontWeight={700} color="primary" sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}>
                  Items ({activeCount})
                </Typography>
                <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.8 }}>
                  {diff && diff.total > 0 && (
                    <Chip
                      label={`${diff.total} change${diff.total > 1 ? "s" : ""}`}
                      size="small"
                      color="warning"
                      sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                    />
                  )}
                  {canEdit && !editMode && (
                    <Button size="small" startIcon={<Edit sx={{ fontSize: 13 }} />} onClick={startEdit} disabled={isLoading}
                      sx={{ fontSize: 11, py: 0.3, px: 1, borderRadius: 1.5, minHeight: 26 }}>
                      Edit Items
                    </Button>
                  )}
                  {editMode && (
                    <>
                      <Button size="small" onClick={cancelEdit} disabled={editSaving}
                        sx={{ fontSize: 11, py: 0.3, px: 1, borderRadius: 1.5, minHeight: 26 }}>
                        Cancel
                      </Button>
                      <Button size="small" variant="contained" onClick={saveEdits} disabled={editSaving}
                        startIcon={editSaving ? <CircularProgress size={11} color="inherit" /> : <Save sx={{ fontSize: 13 }} />}
                        sx={{ fontSize: 11, py: 0.3, px: 1, borderRadius: 1.5, minHeight: 26 }}>
                        {editSaving ? "Saving…" : "Save"}
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Product</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11 }}>Qty</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>Price</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>Subtotal</TableCell>
                  {editMode && <TableCell sx={{ width: 36, p: 0 }} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={editMode ? 5 : 4} align="center" sx={{ py: 3 }}>
                      <Typography variant="caption" color="text.secondary">No items</Typography>
                    </TableCell>
                  </TableRow>
                ) : displayItems.map((item, idx) => {
                  const name  = item.product?.title || item.product?.name || "Item";
                  const price = item.priceAtPurchase || item.product?.price || 0;
                  const qty   = item.quantity || 1;
                  const isDel = !!item._del;
                  const isNew = !!item._new;
                  return (
                    <TableRow key={item._id || idx} sx={{
                      opacity: isDel ? 0.45 : 1,
                      bgcolor: isNew ? "rgba(99,102,241,0.04)" : isDel ? "rgba(239,68,68,0.03)" : "transparent",
                      transition: "all 0.15s",
                    }}>
                      <TableCell sx={{ py: 1.2, textDecoration: isDel ? "line-through" : "none" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {item.product?.images?.[0] ? (
                            <Avatar src={item.product.images[0]} variant="rounded" sx={{ width: 30, height: 30, borderRadius: 1 }} />
                          ) : (
                            <Avatar variant="rounded" sx={{ width: 30, height: 30, borderRadius: 1, bgcolor: "action.hover", fontSize: 11 }}>
                              {name.charAt(0)}
                            </Avatar>
                          )}
                          <Box>
                            <Typography variant="caption" fontWeight={600} sx={{ lineHeight: 1.3, display: "block" }}>{name}</Typography>
                            {isNew && <Typography variant="caption" sx={{ color: "primary.main", fontSize: 10, fontWeight: 700 }}>+ NEW</Typography>}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        {editMode && !isDel ? (
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.3 }}>
                            <IconButton size="small" onClick={() => adjustQty(item._id, -1)}
                              sx={{ width: 20, height: 20, borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                              <Remove sx={{ fontSize: 11 }} />
                            </IconButton>
                            <TextField
                              value={qty}
                              onChange={(e) => setExactQty(item._id, e.target.value)}
                              variant="outlined"
                              size="small"
                              inputProps={{ min: 1, style: { textAlign: "center", padding: "1px 2px", fontSize: 12, width: 32 } }}
                              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1, height: 22 } }}
                            />
                            <IconButton size="small" onClick={() => adjustQty(item._id, 1)}
                              sx={{ width: 20, height: 20, borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                              <Add sx={{ fontSize: 11 }} />
                            </IconButton>
                          </Box>
                        ) : (
                          <Typography variant="caption" fontWeight={700}>{qty}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ textDecoration: isDel ? "line-through" : "none" }}>
                        <Typography variant="caption" color="text.secondary">{kes(price)}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ textDecoration: isDel ? "line-through" : "none" }}>
                        <Typography variant="caption" fontWeight={700}>{kes(price * qty)}</Typography>
                      </TableCell>
                      {editMode && (
                        <TableCell sx={{ p: 0.5 }}>
                          <Tooltip title={isDel ? "Restore item" : "Remove item"}>
                            <IconButton size="small" onClick={() => toggleDel(item._id)}
                              sx={{
                                width: 24, height: 24, borderRadius: 1,
                                color: isDel ? "text.secondary" : "error.main",
                                "&:hover": { bgcolor: isDel ? "action.hover" : "rgba(239,68,68,0.1)" },
                              }}>
                              {isDel ? <Add sx={{ fontSize: 13 }} /> : <Delete sx={{ fontSize: 13 }} />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {/* Add-product panel */}
            {editMode && (
              <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid", borderColor: "divider", bgcolor: "background.default" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary"
                    sx={{ textTransform: "uppercase", fontSize: 10, letterSpacing: 0.6, flex: 1 }}>
                    Add Product
                  </Typography>
                  <TextField
                    type="number"
                    size="small"
                    value={addQty}
                    onChange={(e) => setAddQty(Math.max(1, parseInt(e.target.value) || 1))}
                    label="Qty"
                    inputProps={{ min: 1 }}
                    sx={{ width: 68, "& .MuiInputBase-root": { borderRadius: 1.5, fontSize: 12 } }}
                  />
                </Box>
                {/* Search input */}
                <Box sx={{
                  display: "flex", alignItems: "center", gap: 1,
                  px: 1.5, py: 0.8, borderRadius: 1.5,
                  border: "1px solid", borderColor: "divider",
                  bgcolor: "background.paper", mb: 1,
                }}>
                  <Search sx={{ fontSize: 15, color: "text.secondary", flexShrink: 0 }} />
                  <InputBase
                    value={productQuery}
                    onChange={(e) => setProductQuery(e.target.value)}
                    placeholder={prodsLoading ? "Loading…" : `Search ${products.length} products…`}
                    sx={{ flex: 1, fontSize: 13 }}
                    disabled={prodsLoading}
                  />
                  {prodsLoading && <CircularProgress size={13} sx={{ flexShrink: 0 }} />}
                  {productQuery && (
                    <IconButton size="small" onClick={() => setProductQuery("")} sx={{ p: 0.2 }}>
                      <Close sx={{ fontSize: 13 }} />
                    </IconButton>
                  )}
                </Box>
                {/* Suggestion cards */}
                {productSuggestions.length > 0 && (
                  <Box sx={{
                    maxHeight: 220, overflowY: "auto", borderRadius: 1.5,
                    border: "1px solid", borderColor: "divider",
                    "& > *:not(:last-child)": { borderBottom: "1px solid", borderColor: "divider" },
                  }}>
                    {productSuggestions.map((p) => (
                      <Box
                        key={p._id}
                        onClick={() => addItemFromSuggestion(p)}
                        sx={{
                          display: "flex", alignItems: "center", gap: 1.2,
                          px: 1.5, py: 1,
                          cursor: "pointer", bgcolor: "background.paper",
                          transition: "background 0.12s",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        {p.images?.[0] ? (
                          <Avatar src={p.images[0]} variant="rounded" sx={{ width: 32, height: 32, borderRadius: 1, flexShrink: 0 }} />
                        ) : (
                          <Avatar variant="rounded" sx={{ width: 32, height: 32, borderRadius: 1, fontSize: 12, flexShrink: 0, bgcolor: "action.hover" }}>
                            {(p.title || p.name || "?").charAt(0)}
                          </Avatar>
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" fontWeight={600} noWrap sx={{ display: "block" }}>
                            {p.title || p.name}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <Typography variant="caption" color="success.main" fontWeight={700}>{kes(p.price)}</Typography>
                            {p.stock !== undefined && (
                              <Typography variant="caption" sx={{ color: p.stock > 0 ? "text.secondary" : "error.main" }}>
                                {p.stock > 0 ? `${p.stock} in stock` : "Out of stock"}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{
                          width: 24, height: 24, borderRadius: 1,
                          bgcolor: "primary.main", display: "flex",
                          alignItems: "center", justifyContent: "center", flexShrink: 0,
                        }}>
                          <Add sx={{ fontSize: 14, color: "white" }} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
                {productQuery.trim() && productSuggestions.length === 0 && !prodsLoading && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", py: 1 }}>
                    No matching products
                  </Typography>
                )}
              </Box>
            )}
          </Paper>

          {/* Totals */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" color="text.secondary">Subtotal</Typography>
                <Typography variant="caption">{kes(subtotal)}</Typography>
              </Box>
              {deliveryFee > 0 && (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="caption" color="text.secondary">Delivery Fee</Typography>
                  <Typography variant="caption">{kes(deliveryFee)}</Typography>
                </Box>
              )}
              {discount > 0 && (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="caption" color="text.secondary">Discount</Typography>
                  <Typography variant="caption" sx={{ color: "#22c55e" }}>- {kes(discount)}</Typography>
                </Box>
              )}
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" color="text.secondary">VAT ({Math.round(COMPANY.vatRate * 100)}%)</Typography>
                <Typography variant="caption" color="text.secondary">{kes(total * COMPANY.vatRate)}</Typography>
              </Box>
              <Divider />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" fontWeight={800}>Total</Typography>
                <Typography variant="body2" fontWeight={800} color="primary">{kes(total)}</Typography>
              </Box>
              {/* Cash tendered + print */}
              <Divider />
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  type="number"
                  size="small"
                  label="Cash tendered"
                  value={cashTendered || ""}
                  onChange={(e) => setCashTendered(Number(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  sx={{ flex: 1, "& .MuiInputBase-root": { borderRadius: 1.5, fontSize: 13 } }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={printLoading ? <CircularProgress size={12} /> : <Print sx={{ fontSize: 14 }} />}
                  onClick={handlePrintReceipt}
                  disabled={printLoading}
                  sx={{ borderRadius: 1.5, fontWeight: 700, fontSize: 12, whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  Print Receipt
                </Button>
              </Box>
              {cashTendered > 0 && cashTendered >= total && (
                <Box sx={{ display: "flex", justifyContent: "space-between", bgcolor: "rgba(34,197,94,0.08)", px: 1.5, py: 0.8, borderRadius: 1.5 }}>
                  <Typography variant="caption" fontWeight={700} color="success.main">Change</Typography>
                  <Typography variant="caption" fontWeight={800} color="success.main">{kes(cashTendered - total)}</Typography>
                </Box>
              )}
            </Stack>
          </Paper>

          {/* Payment */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <CreditCard sx={{ fontSize: 16, color: "primary.main" }} />
              <Typography variant="caption" fontWeight={700} color="primary" sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}>Payment</Typography>
            </Box>
            <Stack spacing={0.8}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="caption" color="text.secondary">Method</Typography>
                <PaymentChip method={order.paymentMethod} />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Typography variant="caption" fontWeight={700}
                  sx={{ color: order.paymentStatus === "paid" ? "#22c55e" : order.paymentStatus === "failed" ? "#ef4444" : "#f59e0b" }}>
                  {(order.paymentStatus || "Pending").toUpperCase()}
                </Typography>
              </Box>
              {order.transactionRef && (
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="caption" color="text.secondary">Reference</Typography>
                  <Typography variant="caption" fontFamily="monospace" fontWeight={600}>{order.transactionRef}</Typography>
                </Box>
              )}
            </Stack>
          </Paper>

          {/* Status Update */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <Edit sx={{ fontSize: 16, color: "primary.main" }} />
              <Typography variant="caption" fontWeight={700} color="primary" sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}>Update Status</Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel sx={{ fontSize: 13 }}>Status</InputLabel>
                <Select value={newStatus} label="Status" onChange={(e) => setNewStatus(e.target.value)} disabled={isLoading}
                  sx={{ fontSize: 13, borderRadius: 2 }}>
                  {Object.entries(STATUS_CONFIG).map(([k]) => (
                    <MenuItem key={k} value={k} sx={{ fontSize: 13 }}><StatusChip status={k} /></MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" size="small" disabled={isLoading || newStatus === order.status}
                onClick={() => onStatusChange(order._id, newStatus)}
                sx={{ borderRadius: 2, fontWeight: 700, whiteSpace: "nowrap", minWidth: 110 }}>
                {isLoading ? "Saving…" : "Update Status"}
              </Button>
            </Box>
          </Paper>

          {/* Rider */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <DirectionsBike sx={{ fontSize: 16, color: "primary.main" }} />
              <Typography variant="caption" fontWeight={700} color="primary" sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}>Rider Assignment</Typography>
              {order.rider && (
                <Chip label={order.rider.name || "Assigned"} size="small"
                  sx={{ ml: "auto", fontSize: 11, height: 22, bgcolor: "rgba(34,197,94,0.12)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.3)" }} />
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel sx={{ fontSize: 13 }}>Rider</InputLabel>
                <Select value={newRider} label="Rider" onChange={(e) => setNewRider(e.target.value)} disabled={isLoading}
                  sx={{ fontSize: 13, borderRadius: 2 }}>
                  <MenuItem value="" sx={{ fontSize: 13 }}><em>Unassigned</em></MenuItem>
                  {riders.map((r) => (
                    <MenuItem key={r._id} value={r._id} sx={{ fontSize: 13 }}>{r.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="outlined" size="small" disabled={isLoading || newRider === (order.rider?._id || "")}
                onClick={() => onRiderAssign(order._id, newRider)}
                sx={{ borderRadius: 2, fontWeight: 700, whiteSpace: "nowrap", minWidth: 110 }}>
                {isLoading ? "Saving…" : "Assign Rider"}
              </Button>
            </Box>
            {order.rider && (
              <Button
                size="small"
                startIcon={<MyLocation sx={{ fontSize: 14 }} />}
                onClick={handleTrackRider}
                sx={{ mt: 1, borderRadius: 1.5, fontSize: 12, fontWeight: 700, color: "primary.main" }}
              >
                Track {order.rider.name} live
              </Button>
            )}
          </Paper>

          {/* Timeline */}
          {order.statusHistory && order.statusHistory.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <Timeline sx={{ fontSize: 16, color: "primary.main" }} />
                <Typography variant="caption" fontWeight={700} color="primary" sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}>Timeline</Typography>
              </Box>
              <Stack spacing={1.5}>
                {order.statusHistory.map((h, idx) => {
                  const cfg = STATUS_CONFIG[h.status] || STATUS_CONFIG.created;
                  return (
                    <Box key={idx} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", mt: 0.6, flexShrink: 0, bgcolor: cfg.color, boxShadow: `0 0 6px ${cfg.color}` }} />
                      <Box>
                        <Typography variant="caption" fontWeight={700} sx={{ color: cfg.color }}>{cfg.label}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{fmtDateTime(h.changedAt || h.timestamp)}</Typography>
                        {h.note && <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{h.note}</Typography>}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function ManageOrders() {
  const [orders, setOrders]     = useState([]);
  const [riders, setRiders]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  // Filters
  const [search, setSearch]           = useState("");
  const [statusTab, setStatusTab]     = useState("all");
  const [dateFrom, setDateFrom]       = useState("");
  const [dateTo, setDateTo]           = useState("");
  const [pageSize, setPageSize]       = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage]               = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  // Selection
  const [selected, setSelected]           = useState([]);
  const [bulkStatus, setBulkStatus]       = useState("");
  const [bulkRider, setBulkRider]         = useState("");
  const [bulkLoading, setBulkLoading]     = useState(false);

  // Per-row action state
  const [actionLoading, setActionLoading] = useState({});

  // Drawer
  const [drawerOrder, setDrawerOrder]   = useState(null);
  const [drawerOpen, setDrawerOpen]     = useState(false);

  // Socket / real-time
  const socketRef                          = useRef(null);
  const joinedRoomsRef                     = useRef(new Set());
  const [liveConnected, setLiveConnected]  = useState(false);
  const [newOrderBanner, setNewOrderBanner] = useState(false);

  const { showAlert } = useSnackbar();

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [oRes, rRes] = await Promise.all([fetchOrders(), fetchRidersAdmin()]);
      const newOrders = oRes.data || [];
      setOrders(newOrders);
      setRiders(rRes.data?.riders || []);
      return newOrders;
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || "Failed to load orders";
      setError(msg);
      showAlert(msg, "error");
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Socket setup ──────────────────────────────────────────────────────────
  useEffect(() => {
    const baseURL = (api.defaults.baseURL || window.location.origin).replace(/\/+$/, "");
    const socket = io(baseURL, {
      transports: ["polling", "websocket"], // polling first so cold-start backends can handshake
      reconnectionDelay: 1500,
      reconnectionAttempts: Infinity,
      timeout: 10000,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setLiveConnected(true);
      // Authenticate so backend can add admin to the "admin" room
      try {
        const stored = localStorage.getItem("user");
        const userId = stored ? JSON.parse(stored)?._id : null;
        if (userId) socket.emit("auth", userId);
      } catch { /* ignore parse errors */ }
      // Re-join any order rooms we had before disconnect
      joinedRoomsRef.current.forEach((id) => socket.emit("order:join", id));
    });

    socket.on("disconnect", () => setLiveConnected(false));

    // Real-time order status patch
    socket.on("order:statusUpdate", ({ orderId, status }) => {
      setOrders((prev) =>
        prev.map((o) => (o._id === orderId ? { ...o, status } : o))
      );
      setDrawerOrder((d) =>
        d?._id === orderId ? { ...d, status } : d
      );
    });

    // Real-time payment status patch
    socket.on("order:paymentUpdate", ({ orderId, status: paymentStatus }) => {
      setOrders((prev) =>
        prev.map((o) => (o._id === orderId ? { ...o, paymentStatus } : o))
      );
      setDrawerOrder((d) =>
        d?._id === orderId ? { ...d, paymentStatus } : d
      );
    });

    // New order placed (customer's own notification:new — fallback)
    socket.on("notification:new", (data) => {
      if (data?.type === "order_placed") {
        setNewOrderBanner(true);
        load();
      }
    });

    // Admin-specific: new order broadcast to "admin" room
    socket.on("admin:newOrder", ({ order: newOrder } = {}) => {
      setNewOrderBanner(true);
      if (newOrder) {
        setOrders((prev) => {
          const exists = prev.some((o) => o._id === newOrder._id);
          return exists ? prev : [newOrder, ...prev];
        });
      } else {
        load();
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      joinedRoomsRef.current.clear();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Join socket rooms for all loaded orders so we receive real-time updates
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;
    const active = orders.filter((o) => !["completed", "cancelled"].includes(o.status));
    active.forEach((o) => {
      if (!joinedRoomsRef.current.has(o._id)) {
        socket.emit("order:join", o._id);
        joinedRoomsRef.current.add(o._id);
      }
    });
  }, [orders]);

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const s = { total: orders.length, pending: 0, paid: 0, shipped: 0, completed: 0, cancelled: 0, revenue: 0 };
    for (const o of orders) {
      if (o.status === "created") s.pending++;
      else if (STATUS_CONFIG[o.status]) s[o.status] = (s[o.status] || 0) + 1;
      if (o.status !== "cancelled") s.revenue += o.total || 0;
    }
    return s;
  }, [orders]);

  // ── Filter ────────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo + "T23:59:59") : null;
    return orders.filter((o) => {
      if (statusTab !== "all" && o.status !== statusTab) return false;
      if (q) {
        const matchId = o._id?.toLowerCase().includes(q);
        const matchName = o.user?.name?.toLowerCase().includes(q);
        const matchPhone = o.user?.phone?.includes(search.trim());
        const matchEmail = o.user?.email?.toLowerCase().includes(q);
        if (!matchId && !matchName && !matchPhone && !matchEmail) return false;
      }
      if (from && new Date(o.createdAt) < from) return false;
      if (to && new Date(o.createdAt) > to) return false;
      return true;
    });
  }, [orders, search, statusTab, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, statusTab, dateFrom, dateTo, pageSize]);

  // ── Optimistic patch ──────────────────────────────────────────────────────
  const patchOrder = (id, updater) =>
    setOrders((prev) => prev.map((o) => (o._id === id ? updater(o) : o)));

  // ── Status change ─────────────────────────────────────────────────────────
  const handleStatusChange = async (orderId, newStatus) => {
    const old = orders.find((o) => o._id === orderId);
    if (!old || old.status === newStatus) return;
    setActionLoading((p) => ({ ...p, [orderId]: true }));
    patchOrder(orderId, (o) => ({ ...o, status: newStatus }));
    // Sync drawer
    if (drawerOrder?._id === orderId) setDrawerOrder((d) => d ? { ...d, status: newStatus } : d);
    try {
      await updateOrderStatus(orderId, newStatus);
      showAlert(`Status updated to ${STATUS_CONFIG[newStatus]?.label}`, "success");
    } catch (err) {
      patchOrder(orderId, () => old);
      if (drawerOrder?._id === orderId) setDrawerOrder((d) => d ? { ...d, status: old.status } : d);
      showAlert(err?.response?.data?.message || "Failed to update status", "error");
    } finally {
      setActionLoading((p) => ({ ...p, [orderId]: false }));
    }
  };

  // ── Rider assignment ──────────────────────────────────────────────────────
  const handleRiderAssign = async (orderId, riderId) => {
    const old = orders.find((o) => o._id === orderId);
    if (!old) return;
    const riderObj = riders.find((r) => r._id === riderId) || null;
    setActionLoading((p) => ({ ...p, [orderId]: true }));
    patchOrder(orderId, (o) => ({ ...o, rider: riderObj }));
    if (drawerOrder?._id === orderId) setDrawerOrder((d) => d ? { ...d, rider: riderObj } : d);
    try {
      await assignRiderToOrder(orderId, riderId);
      showAlert(riderId ? `Rider ${riderObj?.name} assigned` : "Rider removed", "success");
    } catch (err) {
      patchOrder(orderId, () => old);
      if (drawerOrder?._id === orderId) setDrawerOrder((d) => d ? { ...d, rider: old.rider } : d);
      showAlert(err?.response?.data?.message || "Failed to assign rider", "error");
    } finally {
      setActionLoading((p) => ({ ...p, [orderId]: false }));
    }
  };

  // ── Items saved ───────────────────────────────────────────────────────────
  const handleItemsSaved = useCallback(async (orderId) => {
    const newOrders = await load();
    const updated = newOrders.find((o) => o._id === orderId);
    if (updated) setDrawerOrder(updated);
    showAlert("Order items updated", "success");
  }, [load]);

  // ── Bulk actions ──────────────────────────────────────────────────────────
  const handleBulkStatusUpdate = async () => {
    if (!bulkStatus || !selected.length) return;
    setBulkLoading(true);
    const snapshot = Object.fromEntries(
      orders.filter((o) => selected.includes(o._id)).map((o) => [o._id, { ...o }])
    );
    setOrders((prev) =>
      prev.map((o) => selected.includes(o._id) ? { ...o, status: bulkStatus } : o)
    );
    try {
      await Promise.all(selected.map((id) => updateOrderStatus(id, bulkStatus)));
      showAlert(`${selected.length} orders updated to ${STATUS_CONFIG[bulkStatus]?.label}`, "success");
      setSelected([]);
      setBulkStatus("");
    } catch (err) {
      setOrders((prev) => prev.map((o) => snapshot[o._id] ? snapshot[o._id] : o));
      showAlert("Bulk update failed", "error");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkRiderAssign = async () => {
    if (!bulkRider || !selected.length) return;
    setBulkLoading(true);
    const riderObj = riders.find((r) => r._id === bulkRider) || null;
    const snapshot = Object.fromEntries(
      orders.filter((o) => selected.includes(o._id)).map((o) => [o._id, { ...o }])
    );
    setOrders((prev) =>
      prev.map((o) => selected.includes(o._id) ? { ...o, rider: riderObj } : o)
    );
    try {
      await Promise.all(selected.map((id) => assignRiderToOrder(id, bulkRider)));
      showAlert(`Rider assigned to ${selected.length} orders`, "success");
      setSelected([]);
      setBulkRider("");
    } catch (err) {
      setOrders((prev) => prev.map((o) => snapshot[o._id] ? snapshot[o._id] : o));
      showAlert("Bulk rider assignment failed", "error");
    } finally {
      setBulkLoading(false);
    }
  };

  // ── Row selection ─────────────────────────────────────────────────────────
  const isAllSelected = paginated.length > 0 && paginated.every((o) => selected.includes(o._id));
  const isIndeterminate = paginated.some((o) => selected.includes(o._id)) && !isAllSelected;

  const toggleAll = () => {
    if (isAllSelected) {
      setSelected((prev) => prev.filter((id) => !paginated.map((o) => o._id).includes(id)));
    } else {
      setSelected((prev) => [...new Set([...prev, ...paginated.map((o) => o._id)])]);
    }
  };

  const toggleRow = (id) =>
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  // ── Open drawer ───────────────────────────────────────────────────────────
  const openDrawer = (order) => {
    setDrawerOrder(order);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => setDrawerOrder(null), 300);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ px: { xs: 0, md: 0 }, py: 0 }}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: -0.5 }}>
              Order Management
            </Typography>
            {/* Live connection indicator */}
            <Tooltip title={liveConnected ? "Live updates active" : "Reconnecting…"}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.3, borderRadius: 5,
                bgcolor: liveConnected ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.10)",
                border: "1px solid", borderColor: liveConnected ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.25)" }}>
                {liveConnected ? (
                  <>
                    <FiberManualRecord sx={{ fontSize: 9, color: "#22c55e",
                      animation: "pulse 2s infinite", "@keyframes pulse": {
                        "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.35 } } }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#22c55e" }}>LIVE</Typography>
                  </>
                ) : (
                  <>
                    <WifiOff sx={{ fontSize: 11, color: "#ef4444" }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#ef4444" }}>Offline</Typography>
                  </>
                )}
              </Box>
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {stats.total} total orders · {kes(stats.revenue)} revenue (excl. cancelled)
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexShrink: 0 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownload />}
            onClick={() => exportCSV(filtered)}
            disabled={filtered.length === 0}
            sx={{ borderRadius: 2, fontWeight: 700, fontSize: 12 }}
          >
            Export CSV
          </Button>
          <Badge badgeContent={newOrderBanner ? "!" : 0} color="error">
            <IconButton onClick={() => { load(); setNewOrderBanner(false); }} disabled={loading} sx={{ borderRadius: 2 }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Badge>
        </Box>
      </Box>

      {/* ── New order banner ───────────────────────────────────────────── */}
      {newOrderBanner && (
        <Alert
          icon={<NotificationsActive sx={{ fontSize: 18 }} />}
          severity="info"
          onClose={() => setNewOrderBanner(false)}
          action={
            <Button size="small" color="inherit" fontWeight={700} onClick={() => { load(); setNewOrderBanner(false); }}>
              Refresh
            </Button>
          }
          sx={{ mb: 2, borderRadius: 2, fontWeight: 600 }}
        >
          New order received — click Refresh to load it.
        </Alert>
      )}

      {/* ── KPI Cards ──────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2.5, flexWrap: "wrap" }}>
        <KPICard
          label="Total Orders"
          value={stats.total}
          subtext={`${kes(stats.revenue)} revenue`}
          color="#6366f1"
          icon={Receipt}
          active={statusTab === "all"}
          onClick={() => setStatusTab("all")}
        />
        <KPICard
          label="New / Pending"
          value={stats.pending}
          subtext={`${stats.paid} paid & ready`}
          color={STATUS_CONFIG.created.color}
          icon={TrendingUp}
          active={statusTab === "created"}
          onClick={() => setStatusTab("created")}
        />
        <KPICard
          label="In Transit"
          value={stats.shipped}
          subtext="out for delivery"
          color={STATUS_CONFIG.shipped.color}
          icon={LocalShipping}
          active={statusTab === "shipped"}
          onClick={() => setStatusTab("shipped")}
        />
        <KPICard
          label="Completed"
          value={stats.completed}
          subtext="fulfilled orders"
          color={STATUS_CONFIG.completed.color}
          icon={CheckCircleOutline}
          active={statusTab === "completed"}
          onClick={() => setStatusTab("completed")}
        />
        <KPICard
          label="Revenue"
          value={kes(stats.revenue)}
          subtext="excl. cancelled"
          color="#8b5cf6"
          icon={AttachMoney}
        />
      </Box>

      {/* ── Stats Chips ────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", gap: 1, mb: 2.5, flexWrap: "wrap" }}>
        <StatChip
          label="All"
          count={stats.total}
          color="#667eea"
          active={statusTab === "all"}
          onClick={() => setStatusTab("all")}
        />
        <StatChip
          label="Pending"
          count={stats.pending}
          color={STATUS_CONFIG.created.color}
          active={statusTab === "created"}
          onClick={() => setStatusTab("created")}
        />
        <StatChip
          label="Paid"
          count={stats.paid}
          color={STATUS_CONFIG.paid.color}
          active={statusTab === "paid"}
          onClick={() => setStatusTab("paid")}
        />
        <StatChip
          label="Shipped"
          count={stats.shipped}
          color={STATUS_CONFIG.shipped.color}
          active={statusTab === "shipped"}
          onClick={() => setStatusTab("shipped")}
        />
        <StatChip
          label="Completed"
          count={stats.completed}
          color={STATUS_CONFIG.completed.color}
          active={statusTab === "completed"}
          onClick={() => setStatusTab("completed")}
        />
        <StatChip
          label="Cancelled"
          count={stats.cancelled}
          color={STATUS_CONFIG.cancelled.color}
          active={statusTab === "cancelled"}
          onClick={() => setStatusTab("cancelled")}
        />
      </Box>

      {/* ── Filter Bar ─────────────────────────────────────────────────── */}
      <Paper
        variant="outlined"
        sx={{ borderRadius: 2, mb: 2, overflow: "hidden" }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {/* Search */}
          <Box
            sx={{
              flex: 1,
              minWidth: 200,
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.8,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.default",
            }}
          >
            <Search sx={{ fontSize: 17, color: "text.secondary", flexShrink: 0 }} />
            <InputBase
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by Order ID, customer name, email, or phone…"
              sx={{ flex: 1, fontSize: 13 }}
            />
            {search && (
              <IconButton size="small" onClick={() => setSearch("")} sx={{ p: 0.3 }}>
                <Close sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Box>

          {/* Advanced filters toggle */}
          <Button
            size="small"
            variant={showFilters ? "contained" : "outlined"}
            startIcon={<FilterList />}
            endIcon={showFilters ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setShowFilters((v) => !v)}
            sx={{ borderRadius: 2, fontWeight: 700, fontSize: 12, flexShrink: 0 }}
          >
            Filters
          </Button>

          {/* Page size */}
          <FormControl size="small" sx={{ minWidth: 100, flexShrink: 0 }}>
            <InputLabel sx={{ fontSize: 12 }}>Per page</InputLabel>
            <Select
              value={pageSize}
              label="Per page"
              onChange={(e) => setPageSize(Number(e.target.value))}
              sx={{ fontSize: 12, borderRadius: 2 }}
            >
              {[10, 20, 50, 100].map((n) => (
                <MenuItem key={n} value={n} sx={{ fontSize: 13 }}>{n}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Expanded date filters */}
        <Collapse in={showFilters}>
          <Divider />
          <Box sx={{ px: 2, py: 1.5, display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", bgcolor: "background.default" }}>
            <CalendarToday sx={{ fontSize: 15, color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Date range:</Typography>
            <TextField
              type="date"
              size="small"
              label="From"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160, "& .MuiInputBase-root": { borderRadius: 2, fontSize: 13 } }}
            />
            <TextField
              type="date"
              size="small"
              label="To"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160, "& .MuiInputBase-root": { borderRadius: 2, fontSize: 13 } }}
            />
            {(dateFrom || dateTo) && (
              <Button
                size="small"
                variant="text"
                onClick={() => { setDateFrom(""); setDateTo(""); }}
                sx={{ fontSize: 12 }}
              >
                Clear dates
              </Button>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* ── Error ──────────────────────────────────────────────────────── */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* ── Bulk Actions Bar ───────────────────────────────────────────── */}
      <Collapse in={selected.length > 0}>
        <Paper
          variant="outlined"
          sx={{
            mb: 1.5,
            px: 2,
            py: 1.2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            bgcolor: "rgba(102,126,234,0.06)",
            borderColor: "primary.main",
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" fontWeight={700} color="primary" sx={{ flexShrink: 0 }}>
            {selected.length} selected
          </Typography>
          <Divider orientation="vertical" flexItem />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ fontSize: 12 }}>Set status</InputLabel>
            <Select
              value={bulkStatus}
              label="Set status"
              onChange={(e) => setBulkStatus(e.target.value)}
              disabled={bulkLoading}
              sx={{ fontSize: 12, borderRadius: 2 }}
            >
              <MenuItem value="" sx={{ fontSize: 13 }}><em>Select status…</em></MenuItem>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                <MenuItem key={k} value={k} sx={{ fontSize: 13 }}>{v.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="contained"
            disabled={!bulkStatus || bulkLoading}
            onClick={handleBulkStatusUpdate}
            sx={{ borderRadius: 2, fontWeight: 700, fontSize: 12 }}
          >
            Apply
          </Button>
          <Divider orientation="vertical" flexItem />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ fontSize: 12 }}>Assign rider</InputLabel>
            <Select
              value={bulkRider}
              label="Assign rider"
              onChange={(e) => setBulkRider(e.target.value)}
              disabled={bulkLoading}
              sx={{ fontSize: 12, borderRadius: 2 }}
            >
              <MenuItem value="" sx={{ fontSize: 13 }}><em>Select rider…</em></MenuItem>
              {riders.map((r) => (
                <MenuItem key={r._id} value={r._id} sx={{ fontSize: 13 }}>{r.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="outlined"
            disabled={!bulkRider || bulkLoading}
            onClick={handleBulkRiderAssign}
            sx={{ borderRadius: 2, fontWeight: 700, fontSize: 12 }}
          >
            Assign
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={() => { setSelected([]); setBulkStatus(""); setBulkRider(""); }}
            sx={{ ml: "auto", fontSize: 12 }}
          >
            Clear
          </Button>
        </Paper>
      </Collapse>

      {/* ── Table ──────────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        {loading && (
          <LinearProgress sx={{ height: 2 }} />
        )}
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: 48 }}>
                  <Checkbox
                    size="small"
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={toggleAll}
                    disabled={loading || paginated.length === 0}
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 130 }}>ORDER</TableCell>
                <TableCell sx={{ minWidth: 160 }}>CUSTOMER</TableCell>
                <TableCell align="center" sx={{ minWidth: 70 }}>ITEMS</TableCell>
                <TableCell align="right" sx={{ minWidth: 110 }}>TOTAL</TableCell>
                <TableCell sx={{ minWidth: 110 }}>PAYMENT</TableCell>
                <TableCell sx={{ minWidth: 110 }}>STATUS</TableCell>
                <TableCell sx={{ minWidth: 110 }}>RIDER</TableCell>
                <TableCell sx={{ minWidth: 120 }}>DATE</TableCell>
                <TableCell align="right" sx={{ minWidth: 100 }}>ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && orders.length === 0 ? (
                Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
              ) : paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} sx={{ py: 8, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      No orders match your filters
                    </Typography>
                    {(search || statusTab !== "all" || dateFrom || dateTo) && (
                      <Button
                        size="small"
                        variant="text"
                        sx={{ mt: 1 }}
                        onClick={() => { setSearch(""); setStatusTab("all"); setDateFrom(""); setDateTo(""); }}
                      >
                        Clear all filters
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : paginated.map((o) => {
                const isSelected = selected.includes(o._id);
                const isLoading = !!actionLoading[o._id];
                const customerLabel = o.user?.name || o.user?.phone || "Unknown";
                const itemCount = (o.items || []).length;

                return (
                  <TableRow
                    key={o._id}
                    selected={isSelected}
                    hover
                    sx={{
                      cursor: "pointer",
                      "&.Mui-selected": { bgcolor: "action.selected" },
                      "&.Mui-selected:hover": { bgcolor: "action.selected" },
                      opacity: isLoading ? 0.7 : 1,
                      transition: "opacity 0.15s",
                    }}
                  >
                    {/* Checkbox */}
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        onChange={() => toggleRow(o._id)}
                      />
                    </TableCell>

                    {/* Order ID */}
                    <TableCell onClick={() => openDrawer(o)}>
                      <Typography
                        variant="caption"
                        fontWeight={800}
                        fontFamily="monospace"
                        sx={{ color: "primary.main", letterSpacing: 0.3 }}
                      >
                        {shortId(o._id)}
                      </Typography>
                    </TableCell>

                    {/* Customer */}
                    <TableCell onClick={() => openDrawer(o)}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar
                          sx={{
                            width: 28, height: 28, fontSize: 12, fontWeight: 700,
                            bgcolor: "primary.main", flexShrink: 0,
                          }}
                        >
                          {customerLabel.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" fontWeight={700} noWrap sx={{ display: "block" }}>
                            {customerLabel}
                          </Typography>
                          {o.user?.phone && o.user?.name && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                              {o.user.phone}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Items count */}
                    <TableCell align="center" onClick={() => openDrawer(o)}>
                      <Chip
                        label={itemCount}
                        size="small"
                        sx={{
                          height: 22, fontSize: 11, fontWeight: 700,
                          bgcolor: "rgba(102,126,234,0.12)", color: "#667eea",
                          border: "1px solid rgba(102,126,234,0.3)",
                          minWidth: 32,
                        }}
                      />
                    </TableCell>

                    {/* Total */}
                    <TableCell align="right" onClick={() => openDrawer(o)}>
                      <Typography variant="caption" fontWeight={800} color="success.main">
                        {kes(o.total)}
                      </Typography>
                    </TableCell>

                    {/* Payment */}
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <PaymentChip method={o.paymentMethod} />
                    </TableCell>

                    {/* Status */}
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Select
                        size="small"
                        value={o.status || "created"}
                        disabled={isLoading}
                        onChange={(e) => { e.stopPropagation(); handleStatusChange(o._id, e.target.value); }}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          borderRadius: 5,
                          height: 28,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: STATUS_CONFIG[o.status]?.border || STATUS_CONFIG.created.border,
                          },
                          "& .MuiSelect-select": {
                            py: 0.5, px: 1.2,
                            color: STATUS_CONFIG[o.status]?.color || STATUS_CONFIG.created.color,
                          },
                          bgcolor: STATUS_CONFIG[o.status]?.bg || STATUS_CONFIG.created.bg,
                        }}
                        renderValue={() => STATUS_CONFIG[o.status]?.label || o.status}
                      >
                        {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                          <MenuItem key={k} value={k} sx={{ fontSize: 13 }}>
                            <StatusChip status={k} />
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>

                    {/* Rider */}
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Select
                        size="small"
                        value={o.rider?._id || ""}
                        displayEmpty
                        disabled={isLoading}
                        onChange={(e) => { e.stopPropagation(); handleRiderAssign(o._id, e.target.value); }}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          fontSize: 11,
                          borderRadius: 2,
                          height: 28,
                          minWidth: 100,
                          "& .MuiSelect-select": { py: 0.5, px: 1 },
                        }}
                        renderValue={(v) => {
                          if (!v) return <Typography variant="caption" color="text.disabled">Unassigned</Typography>;
                          const r = riders.find((r) => r._id === v);
                          return (
                            <Typography variant="caption" fontWeight={600}>
                              {r?.name || "Rider"}
                            </Typography>
                          );
                        }}
                      >
                        <MenuItem value="" sx={{ fontSize: 13 }}><em>Unassigned</em></MenuItem>
                        {riders.map((r) => (
                          <MenuItem key={r._id} value={r._id} sx={{ fontSize: 13 }}>
                            {r.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>

                    {/* Date */}
                    <TableCell onClick={() => openDrawer(o)}>
                      <Typography variant="caption" fontWeight={600} noWrap sx={{ display: "block" }}>
                        {timeAgo(o.createdAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                        {fmtDate(o.createdAt)}
                      </Typography>
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                        <Tooltip title="Print receipt">
                          <IconButton
                            size="small"
                            onClick={() => printReceipt(o)}
                            sx={{
                              borderRadius: 1.5,
                              color: "text.secondary",
                              "&:hover": { color: "text.primary", bgcolor: "action.hover" },
                            }}
                          >
                            <Print sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View order details">
                          <IconButton
                            size="small"
                            onClick={() => openDrawer(o)}
                            sx={{
                              borderRadius: 1.5,
                              bgcolor: "rgba(102,126,234,0.1)",
                              color: "primary.main",
                              "&:hover": { bgcolor: "rgba(102,126,234,0.2)" },
                            }}
                          >
                            <OpenInNew sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Pagination ───────────────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.2,
            borderTop: "1px solid",
            borderColor: "divider",
            flexWrap: "wrap",
            gap: 1,
            bgcolor: "background.default",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Showing {filtered.length === 0 ? 0 : (page - 1) * pageSize + 1}–
            {Math.min(page * pageSize, filtered.length)} of {filtered.length} orders
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, v) => setPage(v)}
            size="small"
            shape="rounded"
            color="primary"
            siblingCount={1}
            boundaryCount={1}
          />
        </Box>
      </Paper>

      {/* ── Order Detail Drawer ─────────────────────────────────────────── */}
      <OrderDetailDrawer
        order={drawerOrder}
        riders={riders}
        open={drawerOpen}
        onClose={closeDrawer}
        onStatusChange={handleStatusChange}
        onRiderAssign={handleRiderAssign}
        onItemsSaved={handleItemsSaved}
        actionLoading={actionLoading}
      />
    </Box>
  );
}
